import * as React from 'react';
import { Capacitor } from '@capacitor/core';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import {
  TRACKING_CONSENT_EVENT,
  flushPendingGoogleAdsConversions,
  getTrackingConsentStatus,
  hasTrackingConsent,
  applyLiveAdIdentity,
  clearAdIdentityState,
  noteAdIdentitySignedOut,
  resolveAdIdentityPermission,
  type AdIdentityPermission,
} from '@/utils/trackingManager';
import { syncNativeFirebaseAnalyticsConsent } from '@/utils/nativeFirebaseAnalytics';
import { isAdExcludedPath, shouldSuppressSensitiveAuthTelemetry } from '@/utils/urlPrivacy';
import { googleAdsKindLabel } from '@/utils/googleAdsConversions';
import { getWebAnalyticsHostExclusion } from '@/utils/analyticsBuildEnvironment';

/**
 * MarketingScripts - Dynamically injects tracking pixels and custom scripts
 *
 * Reads active marketing integrations from the database via the
 * get_active_marketing_scripts() RPC function (which strips server tokens)
 * and injects the appropriate scripts into the page head/body.
 *
 * Security model: Only admins can create/modify scripts (RLS enforced on
 * the marketing_integrations table). The RPC function is read-only and
 * excludes server_tokens.
 */

interface ActiveScript {
  id: string;
  platform: string;
  name: string;
  config: Record<string, string>;
  placement: 'head' | 'body_start' | 'body_end';
  custom_script: string | null;
}

declare global {
  interface Window {
    __JP_META_USER_DATA__?: {
      em?: string;
      external_id?: string;
    };
    __JP_TIKTOK_USER_DATA__?: {
      email?: string;
      external_id?: string;
    };
    AF?: ((plugin: 'pba', method: string, payload?: unknown) => void) & {
      q?: unknown[];
      id?: unknown;
      plugins?: Record<string, unknown>;
    };
    AppsFlyerSdkObject?: string;
    __JP_META_PIXEL_IDS__?: string[];
    __JP_GA4_MEASUREMENT_IDS__?: string[];
    __JP_GOOGLE_ADS_CONFIGS__?: Array<{
      conversionId: string;
      conversionLabel: string | null;
      checkoutConversionLabel: string | null;
      leadConversionLabel: string | null;
      trialConversionLabel: string | null;
      purchaseConversionLabel: string | null;
      subscriptionPurchaseConversionLabel: string | null;
      /** Set only when the kind's label AND server action id are configured. */
      journeyPurchaseConversionLabel?: string | null;
      journeyUpsellConversionLabel?: string | null;
      journeyTrialConversionLabel?: string | null;
    }>;
  }
}

async function fetchActiveScripts(): Promise<ActiveScript[]> {
  const { data, error } = await (supabase as any).rpc('get_active_marketing_scripts');
  if (error) {
    console.warn('Failed to load marketing scripts:', error);
    return [];
  }
  return data || [];
}

const GA4_OR_GOOGLE_TAG_ID_RE = /\b(?:G|GT)-[A-Z0-9]+\b/i;
const GOOGLE_ADS_ID_RE = /\bAW-\d+\b/i;
const GTM_CONTAINER_ID_RE = /\bGTM-[A-Z0-9]+\b/i;
const UA_ID_RE = /\bUA-\d+-\d+\b/i;
const META_PIXEL_ID_RE = /^\d{6,}$/;
const LOCAL_DEV_REFERENCE_RE = /(ws:\/\/localhost|https?:\/\/localhost|127\.0\.0\.1|localhost:8081)/i;
function extractTrackingId(raw: unknown, pattern: RegExp): string | null {
  if (typeof raw !== 'string') return null;
  const match = raw.toUpperCase().match(pattern);
  return match ? match[0] : null;
}

function normalizePlainId(raw: unknown): string | null {
  if (typeof raw !== 'string') return null;
  let value = raw.trim();
  if (
    (value.startsWith('"') && value.endsWith('"')) ||
    (value.startsWith("'") && value.endsWith("'")) ||
    (value.startsWith('`') && value.endsWith('`'))
  ) {
    value = value.slice(1, -1).trim();
  }
  if (!value) return null;
  const lowered = value.toLowerCase();
  if (lowered === 'null' || lowered === 'undefined') return null;
  return value;
}

function normalizeGoogleAnalyticsId(raw: unknown): string | null {
  if (typeof raw !== 'string') return null;
  if (UA_ID_RE.test(raw)) return null;
  return extractTrackingId(raw, GA4_OR_GOOGLE_TAG_ID_RE);
}

function normalizeGoogleAdsId(raw: unknown): string | null {
  return extractTrackingId(raw, GOOGLE_ADS_ID_RE);
}

function normalizeGoogleAdsConversionLabel(raw: unknown): string | null {
  return normalizePlainId(raw);
}

function normalizeGtmContainerId(raw: unknown): string | null {
  return extractTrackingId(raw, GTM_CONTAINER_ID_RE);
}

function normalizeMetaPixelId(raw: unknown): string | null {
  const value = normalizePlainId(raw);
  if (!value) return null;
  const digitsOnly = value.replace(/[^\d]/g, '');
  return META_PIXEL_ID_RE.test(digitsOnly) ? digitsOnly : null;
}

function normalizeAppsFlyerWebDevKey(raw: unknown): string | null {
  return normalizePlainId(raw);
}

function normalizeAdvancedMatchingEmail(raw: unknown): string | null {
  if (typeof raw !== 'string') return null;
  const value = raw.trim().toLowerCase();
  return value.includes('@') ? value : null;
}

function normalizeAdvancedMatchingExternalId(raw: unknown): string | null {
  if (typeof raw !== 'string') return null;
  const value = raw.trim();
  return value ? value : null;
}

function buildMetaAdvancedMatchingData(
  rawEmail: unknown,
  rawExternalId: unknown
): { em?: string; external_id?: string } | null {
  const email = normalizeAdvancedMatchingEmail(rawEmail);
  const externalId = normalizeAdvancedMatchingExternalId(rawExternalId);
  const data = {
    ...(email ? { em: email } : {}),
    ...(externalId ? { external_id: externalId } : {}),
  };

  return data.em || data.external_id ? data : null;
}

// No identity supplied means none is sent: never fall back to a cached
// identity (it may belong to a previous member or an opted-out one).
function buildMetaAdvancedMatchingInit(rawEmail: unknown, rawExternalId: unknown): string {
  const data = buildMetaAdvancedMatchingData(rawEmail, rawExternalId);
  return data ? `,${JSON.stringify(data)}` : '';
}

function buildMetaAdvancedMatchingCache(rawEmail: unknown, rawExternalId: unknown): string {
  const data = buildMetaAdvancedMatchingData(rawEmail, rawExternalId);
  if (!data) return '';
  return `window.__JP_META_USER_DATA__=${JSON.stringify(data)};`;
}

function buildTikTokAdvancedMatchingData(
  rawEmail: unknown,
  rawExternalId: unknown
): { email?: string; external_id?: string } | null {
  const email = normalizeAdvancedMatchingEmail(rawEmail);
  const externalId = normalizeAdvancedMatchingExternalId(rawExternalId);
  const data = {
    ...(email ? { email } : {}),
    ...(externalId ? { external_id: externalId } : {}),
  };

  return data.email || data.external_id ? data : null;
}

function buildTikTokAdvancedMatchingCache(rawEmail: unknown, rawExternalId: unknown): string {
  const data = buildTikTokAdvancedMatchingData(rawEmail, rawExternalId);
  if (!data) return '';
  return `window.__JP_TIKTOK_USER_DATA__=${JSON.stringify(data)};`;
}

// Identify only with the identity supplied now; never from a cache.
function buildTikTokIdentify(rawEmail: unknown, rawExternalId: unknown): string {
  const data = buildTikTokAdvancedMatchingData(rawEmail, rawExternalId);
  return data ? `ttq.identify(${JSON.stringify(data)});` : '';
}

function hasLocalDevReference(raw: unknown): boolean {
  if (typeof raw !== 'string') return false;
  return LOCAL_DEV_REFERENCE_RE.test(raw);
}

function getGoogleConfigIds(scripts: ActiveScript[]): string[] {
  const ids = new Set<string>();

  for (const script of scripts) {
    if (script.platform === 'google_analytics') {
      const gaId = normalizeGoogleAnalyticsId(script.config?.measurement_id);
      if (gaId) ids.add(gaId);
      continue;
    }

    if (script.platform === 'google_ads') {
      const adsId = normalizeGoogleAdsId(script.config?.conversion_id);
      if (adsId) ids.add(adsId);
    }
  }

  return Array.from(ids);
}

function getGa4MeasurementIds(scripts: ActiveScript[]): string[] {
  return scripts
    .filter((script) => script.platform === 'google_analytics')
    .map((script) => normalizeGoogleAnalyticsId(script.config?.measurement_id))
    .filter((id): id is string => Boolean(id));
}

function getGoogleAdsTrackingConfigs(scripts: ActiveScript[]) {
  return scripts
    .filter((script) => script.platform === 'google_ads')
    .map((script) => {
      const conversionId = normalizeGoogleAdsId(script.config?.conversion_id);
      if (!conversionId) return null;

      return {
        conversionId,
        conversionLabel: normalizeGoogleAdsConversionLabel(script.config?.conversion_label),
        checkoutConversionLabel: normalizeGoogleAdsConversionLabel(script.config?.checkout_conversion_label),
        leadConversionLabel: normalizeGoogleAdsConversionLabel(script.config?.lead_conversion_label),
        trialConversionLabel: normalizeGoogleAdsConversionLabel(script.config?.trial_conversion_label),
        purchaseConversionLabel: normalizeGoogleAdsConversionLabel(script.config?.purchase_conversion_label),
        subscriptionPurchaseConversionLabel: normalizeGoogleAdsConversionLabel(
          script.config?.subscription_purchase_conversion_label
        ),
        // Journey funnel sales, each its own action. A label is used only once its
        // server conversion action id is set too (src/utils/googleAdsConversions.ts).
        journeyPurchaseConversionLabel: googleAdsKindLabel(script.config, 'journey_purchase'),
        journeyUpsellConversionLabel: googleAdsKindLabel(script.config, 'journey_upsell'),
        journeyTrialConversionLabel: googleAdsKindLabel(script.config, 'journey_trial'),
      };
    })
    .filter(
      (
        config
      ): config is {
        conversionId: string;
        conversionLabel: string | null;
        checkoutConversionLabel: string | null;
        leadConversionLabel: string | null;
        trialConversionLabel: string | null;
        purchaseConversionLabel: string | null;
        subscriptionPurchaseConversionLabel: string | null;
        journeyPurchaseConversionLabel: string | null;
        journeyUpsellConversionLabel: string | null;
        journeyTrialConversionLabel: string | null;
      } => Boolean(config)
    );
}

/** Platforms that may still load (consent-gated) on ad-excluded pages. */
export const AD_EXCLUDED_PAGE_ALLOWED_PLATFORMS: ReadonlySet<string> = new Set(['google_analytics']);

function shouldLoadWithoutConsent(_script: ActiveScript): boolean {
  return false;
}

/**
 * Generates the inline JS for each built-in platform.
 * Returns raw JS strings (Helmet wraps these in script tags).
 */
function getHeadScripts(
  scripts: ActiveScript[],
  consentGranted: boolean,
  advancedMatchingEmail?: string | null,
  advancedMatchingExternalId?: string | null,
  appsFlyerCustomerUserId?: string | null,
  adExcludedPage = false
): string[] {
  const result: string[] = [];
  const googleConfigIds = getGoogleConfigIds(scripts);

  if (googleConfigIds.length > 0) {
    const defaultConsentState = consentGranted ? 'granted' : 'denied';
    // On an ad-excluded page, Google initializes with ad consent already
    // denied and signals off, instead of correcting it after config.
    const adConsentState = adExcludedPage ? 'denied' : defaultConsentState;
    const consentDefaults =
      `gtag('consent','default',{'analytics_storage':'${defaultConsentState}',` +
      `'ad_storage':'${adConsentState}','ad_user_data':'${adConsentState}',` +
      `'ad_personalization':'${adConsentState}','wait_for_update':500});` +
      (adExcludedPage ? `gtag('set',{'allow_google_signals':false,'allow_ad_personalization_signals':false});` : '');
    // We manually fire SPA page_view events from RouteTracking after the title
    // has settled, so every Google tag disables automatic page_view collection.
    // This avoids duplicate pageviews and stale titles in GA4 for client-side
    // navigations.
    //
    // traffic_type:'internal' tags emulator and automated-browser sessions
    // (Google Play's pre-launch review robots run the app on sdk_gphone
    // emulators and flooded GA4 Realtime with signup_error events on every
    // release) so GA4's Internal Traffic data filter can exclude them. Our own
    // emulator test sessions are excluded by the same rule, which is what we
    // want.
    const configCalls = googleConfigIds
      .map((id) => `gtag('config','${id}',Object.assign({'send_page_view':false},__jpTt));`)
      .join('');
    result.push(
      `window.dataLayer=window.dataLayer||[];` +
      `function gtag(){dataLayer.push(arguments);}` +
      `var __jpTt=(/sdk_gphone|Android SDK built for|emulator/i.test(navigator.userAgent)||navigator.webdriver===true)?{'traffic_type':'internal'}:{};` +
      consentDefaults +
      `gtag('js',new Date());` +
      configCalls
    );
  }

  for (const script of scripts) {
    const c = script.config || {};

    switch (script.platform) {
      case 'google_tag_manager': {
        const id = normalizeGtmContainerId(c.container_id);
        if (!id) break;
        result.push(
          `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':new Date().getTime(),event:'gtm.js'});` +
          `var f=d.getElementsByTagName(s)[0],j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';` +
          `j.async=true;j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl;` +
          `f.parentNode.insertBefore(j,f);})(window,document,'script','dataLayer','${id}');`
        );
        break;
      }

      case 'google_analytics':
      case 'google_ads': {
        break;
      }

      case 'meta_pixel': {
        const id = normalizeMetaPixelId(c.pixel_id);
        if (!id) break;
        // Disable Meta's automatic advanced matching / events detection BEFORE init.
        // With autoConfig on, Meta auto-fires events like InitiateCheckout and
        // SubscribedButtonClick on detected form submissions / button clicks WITHOUT
        // an event_id. Our instrumented trackingManager already fires these events
        // (with event_id, deduped with CAPI), so the auto-detected duplicates:
        //   1. Trigger "InitiateCheckout events not being deduplicated" warnings in
        //      Meta Events Manager (auto-fired pixel event has no event_id).
        //   2. Pollute reporting with noise events (SubscribedButtonClick) we never
        //      instrumented.
        // Must be set BEFORE fbq('init') for the setting to take effect.
        // PageView is also owned by RouteTracking/trackingManager so the browser
        // Pixel event and server CAPI event share the same event_id.
        result.push(
          `!function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?` +
          `n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!f._fbq)f._fbq=n;` +
          `n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];t=b.createElement(e);t.async=!0;` +
          `t.src=v;s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s)}` +
          `(window,document,'script','https://connect.facebook.net/en_US/fbevents.js');` +
          buildMetaAdvancedMatchingCache(advancedMatchingEmail, advancedMatchingExternalId) +
          // Meta auto-fires PageView on history.pushState/replaceState. Our
          // RouteTracking owns PageView (with event_id + CAPI dedupe and the
          // ad-excluded page rule), so the automatic SPA PageView is off.
          `fbq.disablePushState=true;` +
          `fbq('set','autoConfig',false,'${id}');` +
          `fbq('init','${id}'${buildMetaAdvancedMatchingInit(advancedMatchingEmail, advancedMatchingExternalId)});`
        );
        break;
      }

      case 'tiktok_pixel': {
        const id = normalizePlainId(c.pixel_id);
        if (!id) break;
        result.push(
          `!function(w,d,t){w.TiktokAnalyticsObject=t;var ttq=w[t]=w[t]||[];ttq.methods=` +
          `["page","track","identify","instances","debug","on","off","once","ready","alias",` +
          `"group","enableCookie","disableCookie","holdConsent","revokeConsent","grantConsent"];` +
          `ttq.setAndDefer=function(t,e){t[e]=function(){t.push([e].concat(Array.prototype.slice.call(arguments,0)))}};` +
          `for(var i=0;i<ttq.methods.length;i++)ttq.setAndDefer(ttq,ttq.methods[i]);` +
          `ttq.instance=function(t){for(var e=ttq._i[t]||[],n=0;n<ttq.methods.length;n++)` +
          `ttq.setAndDefer(e,ttq.methods[n]);return e};ttq.load=function(e,n){var r="https://analytics.tiktok.com/i18n/pixel/events.js";` +
          `var o=n&&n.partner;ttq._i=ttq._i||{};ttq._i[e]=[];ttq._i[e]._u=r;ttq._t=ttq._t||{};` +
          `ttq._t[e]=+new Date;ttq._o=ttq._o||{};ttq._o[e]=n||{};var a=document.createElement("script");` +
          `a.type="text/javascript";a.async=!0;a.src=r+"?sdkid="+e+"&lib="+t;` +
          `var s=document.getElementsByTagName("script")[0];s.parentNode.insertBefore(a,s)};` +
          buildTikTokAdvancedMatchingCache(advancedMatchingEmail, advancedMatchingExternalId) +
          buildTikTokIdentify(advancedMatchingEmail, advancedMatchingExternalId) +
          `ttq.load('${id}');ttq.page();}(window,document,'ttq');`
        );
        break;
      }

      case 'appsflyer_web': {
        const webDevKey = normalizeAppsFlyerWebDevKey(c.web_dev_key || c.web_sdk_id);
        const customerUserId = normalizePlainId(appsFlyerCustomerUserId);
        if (!webDevKey) break;
        result.push(
          `window.AppsFlyerSdkObject="AF";` +
          `window.AF=window.AF||function(){(window.AF.q=window.AF.q||[]).push([Date.now()].concat(Array.prototype.slice.call(arguments)))};` +
          `window.AF.id=window.AF.id||{pba:{webAppId:${JSON.stringify(webDevKey)},measurementStatus:true}};` +
          `window.AF.plugins=window.AF.plugins||{};` +
          (customerUserId ? `window.AF('pba','setCustomerUserId',${JSON.stringify(customerUserId)});` : '') +
          `if(!document.querySelector('script[data-marketing-script="appsflyer-web-sdk"]')){` +
          `var jpAf=document.createElement("script"),jpAfFirst=document.getElementsByTagName("script")[0];` +
          `jpAf.async=1;jpAf.src="https://websdk.appsflyersdk.com?st=pba&af_id="+encodeURIComponent(${JSON.stringify(webDevKey)});` +
          `jpAf.setAttribute("data-marketing-script","appsflyer-web-sdk");` +
          `jpAfFirst.parentNode.insertBefore(jpAf,jpAfFirst);` +
          `}`
        );
        break;
      }

      case 'linkedin_insight': {
        const id = normalizePlainId(c.partner_id);
        if (!id) break;
        result.push(
          `_linkedin_partner_id="${id}";window._linkedin_data_partner_ids=window._linkedin_data_partner_ids||[];` +
          `window._linkedin_data_partner_ids.push(_linkedin_partner_id);` +
          `(function(l){if(!l){window.lintrk=function(a,b){window.lintrk.q.push([a,b])};` +
          `window.lintrk.q=[]}var s=document.getElementsByTagName("script")[0];` +
          `var b=document.createElement("script");b.type="text/javascript";b.async=true;` +
          `b.src="https://snap.licdn.com/li.lms-analytics/insight.min.js";` +
          `s.parentNode.insertBefore(b,s);})(window.lintrk);`
        );
        break;
      }

      case 'pinterest_tag': {
        const id = normalizePlainId(c.tag_id);
        if (!id) break;
        result.push(
          `!function(e){if(!window.pintrk){window.pintrk=function(){window.pintrk.queue.push(` +
          `Array.prototype.slice.call(arguments))};var n=window.pintrk;n.queue=[],n.version="3.0";` +
          `var t=document.createElement("script");t.async=!0,t.src=e;` +
          `var r=document.getElementsByTagName("script")[0];r.parentNode.insertBefore(t,r)}}` +
          `("https://s.pinimg.com/ct/core.js");pintrk('load','${id}');pintrk('page');`
        );
        break;
      }

      case 'snapchat_pixel': {
        const id = normalizePlainId(c.pixel_id);
        if (!id) break;
        result.push(
          `(function(e,t,n){if(e.snaptr)return;var a=e.snaptr=function(){a.handleRequest?` +
          `a.handleRequest.apply(a,arguments):a.queue.push(arguments)};a.queue=[];` +
          `var s=t.createElement('script');s.async=!0;s.src=n;` +
          `var r=t.getElementsByTagName('script')[0];r.parentNode.insertBefore(s,r);}` +
          `)(window,document,'https://sc-static.net/scevent.min.js');` +
          `snaptr('init','${id}',{});snaptr('track','PAGE_VIEW');`
        );
        break;
      }

      case 'twitter_pixel': {
        const id = normalizePlainId(c.pixel_id);
        if (!id) break;
        result.push(
          `!function(e,t,n,s,u,a){e.twq||(s=e.twq=function(){s.exe?s.exe.apply(s,arguments):` +
          `s.queue.push(arguments);},s.version='1.1',s.queue=[],u=t.createElement(n),u.async=!0,` +
          `u.src='https://static.ads-twitter.com/uwt.js',a=t.getElementsByTagName(n)[0],` +
          `a.parentNode.insertBefore(u,a))}(window,document,'script');` +
          `twq('config','${id}');`
        );
        break;
      }
    }
  }

  return result;
}

/**
 * Get external script src URLs that need to be loaded.
 * GA4 and Google Ads need the gtag.js library loaded externally.
 */
function getExternalScriptSrcs(scripts: ActiveScript[]): string[] {
  const googleConfigIds = getGoogleConfigIds(scripts);
  if (googleConfigIds.length === 0) return [];

  return [
    `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(googleConfigIds[0])}`,
  ];
}

function useBuiltInScriptInjection(
  scripts: ActiveScript[],
  consentGranted: boolean,
  advancedMatchingEmail?: string | null,
  advancedMatchingExternalId?: string | null,
  appsFlyerCustomerUserId?: string | null
) {
  // Scripts inject once per consent state and script list. The identity at
  // injection time rides along; later identity changes go to the live SDKs
  // below, never through a reinjection (which would run ttq.load/ttq.page and
  // the other bootstraps again and duplicate page views).
  const identityRef = React.useRef({ advancedMatchingEmail, advancedMatchingExternalId, appsFlyerCustomerUserId });
  const injectedIdentityKey = React.useRef<string | null>(null);

  // Declared before the injection effect so it always sees this render's identity.
  React.useEffect(() => {
    identityRef.current = { advancedMatchingEmail, advancedMatchingExternalId, appsFlyerCustomerUserId };
  });

  React.useEffect(() => {
    const identity = identityRef.current;
    injectedIdentityKey.current = JSON.stringify(identity);
    const externalSrcs = getExternalScriptSrcs(scripts);
    const inlineHeadScripts = getHeadScripts(
      scripts,
      consentGranted,
      identity.advancedMatchingEmail,
      identity.advancedMatchingExternalId,
      identity.appsFlyerCustomerUserId,
      isAdExcludedPath(window.location.pathname)
    );
    const injected: HTMLScriptElement[] = [];
    const metaPixelIds = scripts
      .filter((script) => script.platform === 'meta_pixel')
      .map((script) => normalizeMetaPixelId(script.config?.pixel_id))
      .filter((id): id is string => Boolean(id));
    const ga4MeasurementIds = getGa4MeasurementIds(scripts);
    const googleAdsConfigs = getGoogleAdsTrackingConfigs(scripts);

    window.__JP_META_PIXEL_IDS__ = metaPixelIds;
    window.__JP_GA4_MEASUREMENT_IDS__ = ga4MeasurementIds;
    window.__JP_GOOGLE_ADS_CONFIGS__ = googleAdsConfigs;

    externalSrcs.forEach((src) => {
      const script = document.createElement('script');
      script.async = true;
      script.src = src;
      script.setAttribute('data-marketing-script', `builtin-external:${src}`);
      document.head.appendChild(script);
      injected.push(script);
    });

    inlineHeadScripts.forEach((js, index) => {
      const script = document.createElement('script');
      script.textContent = js;
      script.setAttribute('data-marketing-script', `builtin-inline:${index}`);
      document.head.appendChild(script);
      injected.push(script);
    });

    flushPendingGoogleAdsConversions();
    window.setTimeout(flushPendingGoogleAdsConversions, 250);
    if (identity.appsFlyerCustomerUserId && window.AF) {
      window.AF('pba', 'setCustomerUserId', identity.appsFlyerCustomerUserId);
    }

    return () => {
      window.__JP_META_PIXEL_IDS__ = [];
      window.__JP_GA4_MEASUREMENT_IDS__ = [];
      window.__JP_GOOGLE_ADS_CONFIGS__ = [];
      injected.forEach((node) => node.parentNode?.removeChild(node));
    };
  }, [consentGranted, scripts]);

  // Identity that arrives (or changes) after injection: Meta advanced
  // matching (fbq set userData + init), TikTok identify and the AppsFlyer
  // customer user id, on the SDKs already running. Removing identity goes
  // through clearAdIdentityState (useAdIdentityCleanup).
  React.useEffect(() => {
    const key = JSON.stringify({ advancedMatchingEmail, advancedMatchingExternalId, appsFlyerCustomerUserId });
    if (injectedIdentityKey.current === key) return;
    injectedIdentityKey.current = key;
    if (!consentGranted) return;
    applyLiveAdIdentity({
      email: advancedMatchingEmail ?? null,
      externalId: advancedMatchingExternalId ?? null,
      appsFlyerCustomerUserId: appsFlyerCustomerUserId ?? null,
    });
  }, [advancedMatchingEmail, advancedMatchingExternalId, appsFlyerCustomerUserId, consentGranted]);
}

function buildCustomNodes(html: string): Node[] {
  const trimmed = html.trim();
  if (!trimmed) return [];

  // Raw JS snippets (no tags) are treated as inline script content.
  if (!trimmed.includes('<')) {
    const script = document.createElement('script');
    script.textContent = trimmed;
    return [script];
  }

  const template = document.createElement('template');
  template.innerHTML = trimmed;

  const cloneExecutableNode = (node: Node): Node => {
    if (node instanceof HTMLScriptElement) {
      const script = document.createElement('script');
      Array.from(node.attributes).forEach((attr) => {
        script.setAttribute(attr.name, attr.value);
      });
      script.textContent = node.textContent || '';
      return script;
    }

    if (node instanceof Element) {
      const clone = node.cloneNode(false) as Element;
      Array.from(node.childNodes).forEach((child) => {
        clone.appendChild(cloneExecutableNode(child));
      });
      return clone;
    }

    return node.cloneNode(true);
  };

  return Array.from(template.content.childNodes).map(cloneExecutableNode);
}

/**
 * Custom snippets can target head/body and may include any HTML,
 * so we inject via DOM APIs instead of Helmet.
 */
function useCustomScriptInjection(scripts: ActiveScript[]) {
  React.useEffect(() => {
    const isLocalhost =
      window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';

    const customScripts = scripts.filter(
      (s) => s.platform === 'custom' && s.custom_script
    );
    const injected: Node[] = [];

    function tagNode(node: Node, id: string) {
      if (node instanceof Element) {
        node.setAttribute('data-marketing-script', id);
      }
    }

    function appendToHead(nodes: Node[]) {
      nodes.forEach((node) => {
        document.head.appendChild(node);
        injected.push(node);
      });
    }

    function appendToBodyStart(nodes: Node[]) {
      const fragment = document.createDocumentFragment();
      nodes.forEach((node) => fragment.appendChild(node));
      const insertedNodes = Array.from(fragment.childNodes);
      document.body.insertBefore(fragment, document.body.firstChild);
      injected.push(...insertedNodes);
    }

    function appendToBodyEnd(nodes: Node[]) {
      const fragment = document.createDocumentFragment();
      nodes.forEach((node) => fragment.appendChild(node));
      const insertedNodes = Array.from(fragment.childNodes);
      document.body.appendChild(fragment);
      injected.push(...insertedNodes);
    }

    customScripts.forEach((script) => {
      // Prevent leaking local-only dev scripts into non-local environments.
      if (!isLocalhost && hasLocalDevReference(script.custom_script)) {
        console.warn(`[MarketingScripts] Skipping local-only custom script: ${script.name}`);
        return;
      }

      const nodes = buildCustomNodes(script.custom_script!);
      nodes.forEach((node) => tagNode(node, script.id));

      if (script.placement === 'body_start') {
        appendToBodyStart(nodes);
        return;
      }

      if (script.placement === 'body_end') {
        appendToBodyEnd(nodes);
        return;
      }

      appendToHead(nodes);
    });

    // GTM noscript iframe (body start)
    const gtm = scripts.find((s) => s.platform === 'google_tag_manager');
    const gtmContainerId = normalizeGtmContainerId(gtm?.config?.container_id);
    if (gtm && gtmContainerId) {
      const noscript = document.createElement('noscript');
      noscript.setAttribute('data-marketing-script', `${gtm.id}-noscript`);
      const iframe = document.createElement('iframe');
      iframe.src = `https://www.googletagmanager.com/ns.html?id=${encodeURIComponent(gtmContainerId)}`;
      iframe.height = '0';
      iframe.width = '0';
      iframe.style.display = 'none';
      iframe.style.visibility = 'hidden';
      noscript.appendChild(iframe);
      document.body.insertBefore(noscript, document.body.firstChild);
      injected.push(noscript, iframe);
    }

    return () => {
      injected.forEach((node) => node.parentNode?.removeChild(node));
    };
  }, [scripts]);
}

/**
 * Whether the signed-in member's identity may be handed to ad platforms
 * (resolveAdIdentityPermission: GPC, an explicit opt-out here, or the
 * account's ad_data_opt_out). 'unknown' until the account read answers, and
 * re-checked when consent changes. Signed out: no identity to hand over.
 */
function useAdIdentityPermission(userId: string | null): {
  value: AdIdentityPermission;
  resolved: boolean;
} {
  const [permission, setPermission] = React.useState<{ userId: string | null; value: AdIdentityPermission }>({
    userId: null,
    value: 'unknown',
  });

  React.useEffect(() => {
    if (!userId || typeof window === 'undefined') return;
    let cancelled = false;
    const resolve = () => {
      void resolveAdIdentityPermission(userId)
        .then((value) => {
          if (!cancelled) setPermission({ userId, value });
        })
        .catch(() => {
          if (!cancelled) setPermission({ userId, value: 'unknown' });
        });
    };
    resolve();
    window.addEventListener(TRACKING_CONSENT_EVENT, resolve);
    return () => {
      cancelled = true;
      window.removeEventListener(TRACKING_CONSENT_EVENT, resolve);
    };
  }, [userId]);

  return userId && permission.userId === userId
    ? { value: permission.value, resolved: true }
    : { value: 'unknown', resolved: false };
}

/**
 * Clears ad identity state on sign-out and when the ad data policy resolves
 * to anything but 'allowed'. An account switch is handled once, in
 * setTrackingUserId (noteActiveAdIdentityUser clears the previous member
 * before the new one is configured); clearing again here would wipe the new
 * member's GA4 / Firebase id after it was set.
 */
function useAdIdentityCleanup(userId: string | null, permission: { value: AdIdentityPermission; resolved: boolean }) {
  const previousUserId = React.useRef<string | null>(userId);
  React.useEffect(() => {
    const previous = previousUserId.current;
    previousUserId.current = userId;
    if (previous !== userId && userId === null) noteAdIdentitySignedOut();
  }, [userId]);

  React.useEffect(() => {
    if (userId && permission.resolved && permission.value !== 'allowed') clearAdIdentityState();
  }, [permission.resolved, permission.value, userId]);
}

export function MarketingScripts() {
  const { user } = useAuth();
  const isNativeRuntime = React.useMemo(() => Capacitor.isNativePlatform(), []);
  const excludedWebHost = React.useMemo(() => {
    if (typeof window === 'undefined') return false;
    return getWebAnalyticsHostExclusion(window.location.hostname, isNativeRuntime) !== null;
  }, [isNativeRuntime]);
  const suppressSensitiveAuthTelemetry = React.useMemo(() => {
    if (typeof window === 'undefined') return false;
    return shouldSuppressSensitiveAuthTelemetry(window.location.href);
  }, []);
  // Landing on a teen/student guide page or a printable: ad platforms
  // (Meta, TikTok, Google Ads, AppsFlyer, plus GTM/LinkedIn/custom tags that
  // could carry ad pixels) never load for this page load. GA4 still loads,
  // behind consent. In-app navigation into these pages is always a full page
  // load (adExcludedPageGuard), so this landing check covers every entry.
  const landedOnAdExcludedPage = React.useMemo(() => {
    if (typeof window === 'undefined') return false;
    return isAdExcludedPath(window.location.pathname);
  }, []);
  const [consentGranted, setConsentGranted] = React.useState<boolean>(() => hasTrackingConsent());

  React.useEffect(() => {
    if (typeof window === 'undefined') return;

    const syncConsent = () => {
      const status = getTrackingConsentStatus();
      setConsentGranted(status === 'granted');
    };

    syncConsent();
    if (isNativeRuntime) {
      // Initialize the SDK on app launch. Later consent changes are applied by
      // syncNativeTrackingConsentStatus before this listener updates the UI.
      void syncNativeFirebaseAnalyticsConsent(getTrackingConsentStatus());
    }
    window.addEventListener(TRACKING_CONSENT_EVENT, syncConsent);
    window.addEventListener('storage', syncConsent);

    return () => {
      window.removeEventListener(TRACKING_CONSENT_EVENT, syncConsent);
      window.removeEventListener('storage', syncConsent);
    };
  }, [isNativeRuntime]);

  const { data: scripts = [] } = useQuery({
    queryKey: ['activeMarketingScripts'],
    queryFn: fetchActiveScripts,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
    // Native builds load no web tags at all. Firebase Analytics, AppsFlyer
    // and RevenueCat report from the native SDKs; a web pixel, gtag or the
    // AppsFlyer Web SDK inside the app would count the same member twice.
    enabled: !suppressSensitiveAuthTelemetry && !isNativeRuntime && !excludedWebHost,
  });

  const activeScripts = React.useMemo(() => {
    if (isNativeRuntime) return [];
    return scripts.filter((script) => {
      if (suppressSensitiveAuthTelemetry) return false;
      if (excludedWebHost) return false;
      if (landedOnAdExcludedPage && !AD_EXCLUDED_PAGE_ALLOWED_PLATFORMS.has(script.platform)) return false;
      if (!consentGranted && !shouldLoadWithoutConsent(script)) return false;
      return true;
    });
  }, [consentGranted, excludedWebHost, isNativeRuntime, landedOnAdExcludedPage, scripts, suppressSensitiveAuthTelemetry]);

  // Scripts load on local consent alone (anonymous). The signed-in member's
  // email and id (Meta advanced matching, TikTok identify, the AppsFlyer
  // customer user id) are added only once the ad data policy allows it.
  const adIdentityPermission = useAdIdentityPermission(isNativeRuntime ? null : user?.id ?? null);
  useAdIdentityCleanup(user?.id ?? null, adIdentityPermission);
  const identityAllowed = adIdentityPermission.value === 'allowed';
  useBuiltInScriptInjection(
    activeScripts,
    consentGranted,
    identityAllowed ? user?.email ?? null : null,
    identityAllowed ? user?.id ?? null : null,
    identityAllowed ? user?.id ?? null : null
  );
  useCustomScriptInjection(activeScripts);

  return null;
}

export default MarketingScripts;
